<script setup lang="ts">
import CreateOrDeleteButton from "@/components/AllowAccessOfficeParts/createOrDeleteButtons/CreateOrDeleteButton.vue"
import ConfirmModal from "@/components/AllowAccessOfficeParts/createOrDeleteButtons/ConfirmModal.vue"
import getAllowAccessOfficeResponse from "@/types/responses/getAllowAccessOffice"
import Api from "@/services/api/ApiServiceFabrick"
import { Ref, ref, computed } from 'vue'
import i18n from '@/lang/i18n';

const emits = defineEmits(['inactiveModal'])
const props = defineProps<{
  accountId: number
  operationOfficeId: number
  allowAccessOffice: getAllowAccessOfficeResponse[]
  createAuthority: boolean
  deleteAuthority: boolean
}>()

const dialogState: Ref<boolean> = ref(false)
const activateDialogKey: Ref<string> = ref("")
const addKey = "add"
const deleteKey = "delete"
const addConfirmMessage = i18n.global.t('pages.allowAccessOffice.confirmationModal.addConfirmMessage')
const deleteConfirmMessage = i18n.global.t('pages.allowAccessOffice.confirmationModal.deleteConfirmMessage')
const status: Ref<string> = ref("approved")
const isLoading: Ref<boolean> = ref(false)
const addOrDeleteMessage: Ref<string> = ref("")
const disabled: Ref<boolean> = ref(false)
const isSucceeded: Ref<boolean> = ref(true)

const confirmText = computed(() => {
  return activateDialogKey.value == addKey ? addConfirmMessage : deleteConfirmMessage
})

const resultMessageColor = computed(() => {
  return isSucceeded.value ? "#3c763d" : "#a94442"
})

const isNotViewPermissionAndAddedIcon = computed(() => {
  return !props.createAuthority && !props.deleteAuthority && props.allowAccessOffice.length
})

function activateDialog (key: string) {
  activateDialogKey.value = key
  dialogState.value = true
}

function inactivateDialog(bool: boolean) {
  dialogState.value = false
  disabled.value = false
  addOrDeleteMessage.value = ""
  if (bool || !isSucceeded.value) emits('inactiveModal', isSucceeded.value)
}

function requestAllowAccessOffice() {
  isLoading.value = true
  if (activateDialogKey.value == addKey) return addAllowAccessOffice()
  deleteAllowAccessOffice()
}

function addAllowAccessOffice() {
  const paramas = {
    account_id: props.accountId,
    operation_office_id: props.operationOfficeId,
    status: status.value,
  }
  Api().createAllowAccessOffice(paramas).then((data: any) => {
    success()
  }).catch((error: any) => {
    errorHandle(error.response)
  })
}

function deleteAllowAccessOffice() {
  if (!props.allowAccessOffice.length) return
  Api().deleteAllowAccessOffice(props.allowAccessOffice[0].id).then((data: any) => {
    success()
  }).catch((error: any) => {
    errorHandle(error.response)
  })
}

function success() {
  isLoading.value = false
  makeMessage(true)
  disabled.value = true
  isSucceeded.value = true
}

function makeMessage(bool: boolean) {
  const resultPrefix = i18n.global.t('pages.allowAccessOffice.confirmationModal.resultPrefix')
  const actionKey = activateDialogKey.value == addKey ?
    i18n.global.t('pages.allowAccessOffice.confirmationModal.approval') :
    i18n.global.t('pages.allowAccessOffice.confirmationModal.delete')
  const result = bool ?
    i18n.global.t('pages.allowAccessOffice.confirmationModal.success'):
    i18n.global.t('pages.allowAccessOffice.confirmationModal.failed')
  addOrDeleteMessage.value = resultPrefix + actionKey + result
}

function failed() {
  isSucceeded.value = false
  isLoading.value = false
  disabled.value = true
}

function errorHandle (res: any) {
  failed()
  switch (res.status) {
    case 401:
      makeMessage(false)
      break
    case 403:
      addOrDeleteMessage.value = i18n.global.t('pages.common.messages.forbidden')
      break
    case 422:
      addOrDeleteMessage.value = res.data.errors
      break
    default:
      makeMessage(false)
  }
}
</script>
<template>
  <div class="d-flex justify-content-end pr-0">
    <span
      v-if="isNotViewPermissionAndAddedIcon"
      class="material-symbols-outlined is-added-check-circle"
    >
      check_circle
    </span>
    <CreateOrDeleteButton
      v-if="props.createAuthority"
      :btnName="i18n.global.t('pages.allowAccessOffice.confirmationModal.add')"
      btnStyle="rgb(179, 184, 187)"
      btnClass="ma-2 exec-btn"
      :actionKey="addKey"
      :isAdded="!props.allowAccessOffice.length"
      @addRequested="activateDialog"
    />
    <CreateOrDeleteButton
      v-if="props.deleteAuthority"
      :btnName="i18n.global.t('pages.allowAccessOffice.confirmationModal.delete')"
      btnStyle="rgb(179, 184, 187) !important"
      btnClass="ma-2"
      btnColor="error"
      :actionKey="deleteKey"
      :isAdded="props.allowAccessOffice.length ? true : false"
      @addRequested="activateDialog"
    />
    <ConfirmModal
      :title="i18n.global.t('common.confirmationModal.title')"
      btnClass='mt-0 ml-1'
      closeBtnClass='mt-0'
      :dialogState="dialogState"
      :actionKey="activateDialogKey"
      :confirmText='confirmText'
      :isLoading="isLoading"
      :message="addOrDeleteMessage"
      :resultMessageColor="resultMessageColor"
      :disabled="disabled"
      @exec="requestAllowAccessOffice"
      @closeModal="inactivateDialog"
    />
  </div>
</template>
<style scooped>
.is-added-check-circle {
  color: #3c763d;
  font-size: 30px;
}
</style>